/**
 * UnitePage - Détail d'une unité de travail
 * Liste des substances avec contexte d'exposition
 */

import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, CardBody } from '../components/ui/Card'
import Breadcrumb from '../components/ui/Breadcrumb'
import { LegalDisclaimer } from '../components/legal'
import {
  duerpAPI,
  substancesAPI,
  DuerpDocumentFull,
  DuerpUnite,
  DuerpSubstance,
  FREQUENCE_OPTIONS,
  FORME_OPTIONS,
  VOIES_EXPOSITION_OPTIONS,
  EPC_OPTIONS,
  EPI_OPTIONS,
} from '../services/duerp'
import { substanceAPI, SubstanceFiche } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { GHSPictogramList } from '../components/ghs/GHSPictogram'

export default function UnitePage() {
  const { id: duerpId, uniteId } = useParams<{ id: string; uniteId: string }>()
  const { isAuthenticated } = useAuthStore()

  const [document, setDocument] = useState<DuerpDocumentFull | null>(null)
  const [unite, setUnite] = useState<DuerpUnite | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [showAddSubstance, setShowAddSubstance] = useState(false)
  const [editingSubstance, setEditingSubstance] = useState<DuerpSubstance | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)

  // Charger le document
  useEffect(() => {
    if (!isAuthenticated || !duerpId) {
      setIsLoading(false)
      return
    }

    const loadData = async () => {
      try {
        setIsLoading(true)
        const result = await duerpAPI.get(duerpId)
        setDocument(result.document)
        const u = result.document.unites.find((u) => u.id === uniteId)
        if (u) {
          setUnite(u)
        } else {
          setError('Unité non trouvée')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur de chargement')
      } finally {
        setIsLoading(false)
      }
    }

    loadData()
  }, [isAuthenticated, duerpId, uniteId])

  // Recharger
  const reload = async () => {
    if (!duerpId) return
    try {
      const result = await duerpAPI.get(duerpId)
      setDocument(result.document)
      const u = result.document.unites.find((u) => u.id === uniteId)
      if (u) setUnite(u)
    } catch (err) {
      console.error('Erreur rechargement:', err)
    }
  }

  // Ajouter une substance
  const handleAddSubstance = async (data: {
    cas_number: string
    nom_substance?: string
    donnees_reglementaires?: Record<string, unknown>
  }) => {
    if (!duerpId || !uniteId) return
    try {
      await substancesAPI.create(duerpId, uniteId, data)
      await reload()
      setShowAddSubstance(false)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur')
    }
  }

  // Modifier une substance
  const handleUpdateSubstance = async (
    substanceId: string,
    data: Partial<DuerpSubstance>
  ) => {
    if (!duerpId || !uniteId) return
    try {
      await substancesAPI.update(duerpId, uniteId, substanceId, data)
      await reload()
      setEditingSubstance(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur')
    }
  }

  // Supprimer une substance
  const handleDeleteSubstance = async (substanceId: string) => {
    if (!duerpId || !uniteId) return
    try {
      await substancesAPI.delete(duerpId, uniteId, substanceId)
      await reload()
      setDeleteConfirm(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erreur')
    }
  }

  // Chargement
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <svg className="animate-spin h-8 w-8 text-blue-600" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    )
  }

  // Erreur
  if (error || !document || !unite) {
    return (
      <div className="max-w-xl mx-auto py-12 px-4">
        <Card className="border-red-200 bg-red-50">
          <CardBody className="text-center py-8">
            <p className="text-red-600">{error || 'Unité non trouvée'}</p>
            <Link to={`/duerp/${duerpId}`} className="mt-4 inline-block text-sm text-red-700 underline">
              Retour au DUERP
            </Link>
          </CardBody>
        </Card>
      </div>
    )
  }

  const substances = unite.substances || []
  const nbCMR = substances.filter((s) => s.donnees_reglementaires?.est_cmr).length

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Accueil', href: '/' },
          { label: 'Mes DUERP', href: '/duerp' },
          { label: document.raison_sociale, href: `/duerp/${duerpId}` },
          { label: unite.nom },
        ]}
      />

      {/* En-tête */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{unite.nom}</h1>
          {unite.description && (
            <p className="text-sm text-gray-500 mt-1">{unite.description}</p>
          )}
          {unite.effectif && (
            <p className="text-xs text-gray-500 mt-1">{unite.effectif} personne(s)</p>
          )}
        </div>
        <button
          onClick={() => setShowAddSubstance(true)}
          className="btn-primary flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Ajouter une substance
        </button>
      </div>

      {/* Alerte CMR */}
      {nbCMR > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p>
              <span className="font-medium">{nbCMR} substance(s) CMR</span> dans cette unité.
              Suivi individuel renforcé obligatoire.
            </p>
          </div>
        </div>
      )}

      {/* Message pédagogique contexte d'exposition */}
      {substances.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p>
              Décrivez le contexte d'exposition pour chaque substance.
              Aucun calcul ni interprétation n'est réalisé par l'outil.
            </p>
          </div>
        </div>
      )}

      {/* Liste des substances */}
      {substances.length === 0 ? (
        <Card>
          <CardBody className="text-center py-12">
            <svg
              className="w-12 h-12 mx-auto text-gray-300 mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Aucune substance
            </h3>
            <p className="text-gray-600 mb-6">
              Ajoutez des substances pour documenter les expositions.
            </p>
            <button onClick={() => setShowAddSubstance(true)} className="btn-primary">
              Ajouter une substance
            </button>
          </CardBody>
        </Card>
      ) : (
        <div className="space-y-4">
          {substances.map((substance) => (
            <SubstanceCard
              key={substance.id}
              substance={substance}
              onEdit={() => setEditingSubstance(substance)}
              onDelete={() => setDeleteConfirm(substance.id)}
            />
          ))}
        </div>
      )}

      {/* Disclaimer légal unité (CDC v1.2 §9.3) */}
      <LegalDisclaimer variant="unit" className="mt-6" />

      {/* Modal ajout substance */}
      {showAddSubstance && (
        <AddSubstanceModal
          onClose={() => setShowAddSubstance(false)}
          onAdd={handleAddSubstance}
        />
      )}

      {/* Modal édition contexte */}
      {editingSubstance && (
        <EditContextModal
          substance={editingSubstance}
          onClose={() => setEditingSubstance(null)}
          onSave={(data) => handleUpdateSubstance(editingSubstance.id, data)}
        />
      )}

      {/* Confirmation suppression */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Retirer cette substance ?
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Les données de contexte d'exposition seront perdues.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={() => handleDeleteSubstance(deleteConfirm)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Retirer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Vérifie si le contexte d'exposition est renseigné
 * Considéré "renseigné" si au moins un champ non vide / une case cochée
 */
function isContexteRenseigne(s: DuerpSubstance): boolean {
  return !!(
    s.activite_tache ||
    s.frequence ||
    s.duree_moyenne ||
    s.forme ||
    s.procedes ||
    (s.voies_exposition && s.voies_exposition.length > 0) ||
    (s.epc_existants && s.epc_existants.length > 0) ||
    (s.epi_portes && s.epi_portes.length > 0) ||
    s.epi_details ||
    s.mesures_prevention ||
    s.observations
  )
}

/**
 * Carte substance
 */
function SubstanceCard({
  substance,
  onEdit,
  onDelete,
}: {
  substance: DuerpSubstance
  onEdit: () => void
  onDelete: () => void
}) {
  const [showDetails, setShowDetails] = useState(false)
  const regl = substance.donnees_reglementaires || {}
  const contexteOK = isContexteRenseigne(substance)

  return (
    <Card className={regl.est_cmr ? 'border-red-200' : ''}>
      <CardBody>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-semibold text-gray-900">
                {substance.nom_substance || substance.cas_number}
              </h3>
              <span className="text-xs text-gray-500 font-mono">
                CAS {substance.cas_number}
              </span>
              {regl.est_cmr && (
                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                  CMR {regl.categorie_cmr}
                </span>
              )}
              {/* Badge statut contexte */}
              {contexteOK ? (
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full font-medium flex items-center gap-1">
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Contexte renseigné
                </span>
              ) : (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full font-medium">
                  À compléter
                </span>
              )}
            </div>

            {/* Résumé contexte */}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
              {substance.frequence && (
                <span>
                  {FREQUENCE_OPTIONS.find((f) => f.value === substance.frequence)?.label}
                </span>
              )}
              {substance.duree_moyenne && <span>{substance.duree_moyenne}</span>}
              {substance.voies_exposition && substance.voies_exposition.length > 0 && (
                <span>
                  {substance.voies_exposition
                    .map((v) => VOIES_EXPOSITION_OPTIONS.find((o) => o.value === v)?.label)
                    .filter(Boolean)
                    .join(', ')}
                </span>
              )}
            </div>

            {/* Pictogrammes GHS */}
            {regl.pictogrammes && regl.pictogrammes.length > 0 && (
              <div className="mt-2">
                <GHSPictogramList codes={regl.pictogrammes} size="sm" />
              </div>
            )}
          </div>

          <div className="flex items-center gap-1">
            {/* Bouton Contexte explicite */}
            <button
              onClick={onEdit}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg flex items-center gap-1.5 ${
                contexteOK
                  ? 'text-gray-600 hover:text-blue-600 hover:bg-blue-50 border border-gray-200'
                  : 'text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200'
              }`}
              title="Décrire le contexte d'exposition"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Contexte
            </button>
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              title="Détails"
            >
              <svg
                className={`w-4 h-4 transition-transform ${showDetails ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg"
              title="Retirer"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
            <Link
              to={`/substance/${substance.cas_number}`}
              target="_blank"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
              title="Voir la fiche substance"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </Link>
          </div>
        </div>

        {/* Détails dépliables */}
        {showDetails && (
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
            {/* Colonne gauche: Contexte d'exposition (saisi) */}
            <div className="space-y-3">
              <h4 className="font-medium text-gray-700">Contexte d'exposition</h4>

              {substance.activite_tache && (
                <div>
                  <span className="text-gray-500">Activité :</span>
                  <span className="ml-2">{substance.activite_tache}</span>
                </div>
              )}

              {substance.forme && (
                <div>
                  <span className="text-gray-500">Forme :</span>
                  <span className="ml-2">
                    {FORME_OPTIONS.find((f) => f.value === substance.forme)?.label || substance.forme}
                  </span>
                </div>
              )}

              {substance.procedes && (
                <div>
                  <span className="text-gray-500">Procédés :</span>
                  <span className="ml-2">{substance.procedes}</span>
                </div>
              )}

              {substance.epc_existants && substance.epc_existants.length > 0 && (
                <div>
                  <span className="text-gray-500">EPC :</span>
                  <span className="ml-2">
                    {substance.epc_existants
                      .map((e) => EPC_OPTIONS.find((o) => o.value === e)?.label)
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </div>
              )}

              {substance.epi_portes && substance.epi_portes.length > 0 && (
                <div>
                  <span className="text-gray-500">EPI :</span>
                  <span className="ml-2">
                    {substance.epi_portes
                      .map((e) => EPI_OPTIONS.find((o) => o.value === e)?.label)
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </div>
              )}

              {substance.mesures_prevention && (
                <div>
                  <span className="text-gray-500">Mesures :</span>
                  <p className="mt-1 text-gray-700">{substance.mesures_prevention}</p>
                </div>
              )}

              {substance.observations && (
                <div>
                  <span className="text-gray-500">Observations :</span>
                  <p className="mt-1 text-gray-700">{substance.observations}</p>
                </div>
              )}
            </div>

            {/* Colonne droite: Informations sources (lecture seule, à titre indicatif) */}
            <div className="space-y-3 bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-700">Informations sources (indicatif)</h4>
              <p className="text-xs text-gray-500">
                Réf. source : {regl.sources?.join(', ') || 'INRS'}{regl.sources?.includes('INRS') || !regl.sources ? ' — Édition 2024-01' : ''}
              </p>
              <p className="text-xs text-gray-500 italic">
                Données à titre informatif uniquement. Toute évolution ultérieure des sources
                officielles n'est pas reflétée automatiquement.
              </p>

              {regl.codes_h && regl.codes_h.length > 0 && (
                <div>
                  <span className="text-gray-500">Mentions de danger :</span>
                  <p className="mt-1 font-mono text-xs">{regl.codes_h.join(', ')}</p>
                </div>
              )}

              {regl.vlep_8h && (
                <div>
                  <span className="text-gray-500">VLEP 8h :</span>
                  <span className="ml-2">
                    {regl.vlep_8h.valeur} {regl.vlep_8h.unite}
                  </span>
                </div>
              )}

              {regl.vlep_court_terme && (
                <div>
                  <span className="text-gray-500">VLEP CT :</span>
                  <span className="ml-2">
                    {regl.vlep_court_terme.valeur} {regl.vlep_court_terme.unite}
                  </span>
                </div>
              )}

              {regl.epi_recommandes && regl.epi_recommandes.length > 0 && (
                <div>
                  <span className="text-gray-500">EPI recommandés :</span>
                  <p className="mt-1">{regl.epi_recommandes.join(', ')}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}

/**
 * Modal recherche et ajout substance
 */
function AddSubstanceModal({
  onClose,
  onAdd,
}: {
  onClose: () => void
  onAdd: (data: {
    cas_number: string
    nom_substance?: string
    donnees_reglementaires?: Record<string, unknown>
  }) => Promise<void>
}) {
  const [query, setQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [searchResults, setSearchResults] = useState<Array<{ cas: string; name: string }>>([])
  const [selectedCAS, setSelectedCAS] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [ficheData, setFicheData] = useState<SubstanceFiche | null>(null)

  // Recherche
  const handleSearch = async () => {
    if (query.length < 2) return

    try {
      setIsSearching(true)
      // Utiliser l'API de recherche existante
      const response = await fetch(
        `/api/v2/search?q=${encodeURIComponent(query)}&sources=inrs,anses,echa`,
        { credentials: 'include' }
      )
      const data = await response.json()

      // Extraire les résultats
      const results: Array<{ cas: string; name: string }> = []
      const seen = new Set<string>()

      if (data.sources) {
        for (const [, sourceData] of Object.entries(data.sources as Record<string, { data?: Array<{ cas_number?: string; cas?: string; name?: string; substance_name?: string }> }>)) {
          if (sourceData.data) {
            for (const item of sourceData.data) {
              const cas = item.cas_number || item.cas
              const substanceName = item.name || item.substance_name || cas || ''
              if (cas && !seen.has(cas)) {
                seen.add(cas)
                results.push({ cas, name: substanceName })
              }
            }
          }
        }
      }

      setSearchResults(results.slice(0, 10))
    } catch (err) {
      console.error('Erreur recherche:', err)
    } finally {
      setIsSearching(false)
    }
  }

  // Sélection CAS
  const handleSelectCAS = async (cas: string, _name: string) => {
    setSelectedCAS(cas)
    setIsLoading(true)

    try {
      // Récupérer la fiche substance
      const fiche = await substanceAPI.getByCAS(cas)
      setFicheData(fiche)
    } catch (err) {
      console.error('Erreur chargement fiche:', err)
      // Continuer même sans fiche complète
      setFicheData(null)
    } finally {
      setIsLoading(false)
    }
  }

  // Ajouter
  const handleAdd = async () => {
    if (!selectedCAS) return

    const donnees: Record<string, unknown> = {}
    if (ficheData) {
      donnees.est_cmr = ficheData.dangers?.cmr?.est_cmr || false
      donnees.categorie_cmr = ficheData.dangers?.cmr?.categorie || null
      donnees.codes_h = ficheData.dangers?.codes_h || []
      donnees.pictogrammes = ficheData.dangers?.pictogrammes?.map((p) => p.code) || []
      if (ficheData.sources?.inrs?.vlep_8h) {
        donnees.vlep_8h = ficheData.sources.inrs.vlep_8h
      }
      if (ficheData.sources?.inrs?.vlep_court_terme) {
        donnees.vlep_court_terme = ficheData.sources.inrs.vlep_court_terme
      }
      donnees.sources = ficheData.meta?.sources_trouvees || []
    }

    await onAdd({
      cas_number: selectedCAS,
      nom_substance: ficheData?.identite?.nom_fr || searchResults.find((r) => r.cas === selectedCAS)?.name,
      donnees_reglementaires: donnees,
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              Ajouter une substance
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Recherche */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Rechercher par nom ou CAS..."
            />
            <button
              onClick={handleSearch}
              disabled={isSearching || query.length < 2}
              className="btn-primary px-4"
            >
              {isSearching ? '...' : 'Rechercher'}
            </button>
          </div>

          {/* Résultats */}
          {searchResults.length > 0 && !selectedCAS && (
            <div className="space-y-2">
              {searchResults.map((result) => (
                <button
                  key={result.cas}
                  onClick={() => handleSelectCAS(result.cas, result.name)}
                  className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <p className="font-medium text-gray-900">{result.name}</p>
                  <p className="text-sm text-gray-500">CAS {result.cas}</p>
                </button>
              ))}
            </div>
          )}

          {/* Prévisualisation sélection */}
          {selectedCAS && (
            <div className="mt-4">
              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <svg className="animate-spin h-6 w-6 text-blue-600" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                </div>
              ) : (
                <Card>
                  <CardBody>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">
                          {ficheData?.identite?.nom_fr || selectedCAS}
                        </h3>
                        <p className="text-sm text-gray-500">CAS {selectedCAS}</p>
                        {ficheData?.dangers?.cmr?.est_cmr && (
                          <span className="inline-block mt-2 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full font-medium">
                            CMR {ficheData.dangers.cmr.categorie}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setSelectedCAS(null)
                          setFicheData(null)
                        }}
                        className="text-sm text-gray-500 hover:text-gray-700"
                      >
                        Changer
                      </button>
                    </div>

                    {ficheData && (
                      <div className="mt-4 text-sm text-gray-600">
                        {ficheData.dangers?.codes_h && ficheData.dangers.codes_h.length > 0 && (
                          <p className="text-xs font-mono text-gray-500">
                            {ficheData.dangers.codes_h.slice(0, 5).join(', ')}
                            {ficheData.dangers.codes_h.length > 5 && '...'}
                          </p>
                        )}
                        <p className="mt-2 text-xs text-gray-500">
                          Données issues de : {ficheData.meta?.sources_trouvees?.join(', ')}
                        </p>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
            >
              Annuler
            </button>
            <button
              onClick={handleAdd}
              disabled={!selectedCAS || isLoading}
              className="btn-primary"
            >
              Ajouter
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * Modal édition contexte d'exposition
 */
type FrequenceType = 'occasionnelle' | 'hebdomadaire' | 'quotidienne' | 'permanente' | ''
type FormeType = 'poudre' | 'liquide' | 'vapeurs' | 'gaz' | 'solide' | 'autre' | ''

interface ContextFormData {
  activite_tache: string
  frequence: FrequenceType
  duree_moyenne: string
  forme: FormeType
  procedes: string
  voies_exposition: string[]
  epc_existants: string[]
  epi_portes: string[]
  epi_details: string
  mesures_prevention: string
  observations: string
}

function EditContextModal({
  substance,
  onClose,
  onSave,
}: {
  substance: DuerpSubstance
  onClose: () => void
  onSave: (data: Partial<DuerpSubstance>) => Promise<void>
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [formData, setFormData] = useState<ContextFormData>({
    activite_tache: substance.activite_tache || '',
    frequence: substance.frequence || '',
    duree_moyenne: substance.duree_moyenne || '',
    forme: substance.forme || '',
    procedes: substance.procedes || '',
    voies_exposition: substance.voies_exposition || [],
    epc_existants: substance.epc_existants || [],
    epi_portes: substance.epi_portes || [],
    epi_details: substance.epi_details || '',
    mesures_prevention: substance.mesures_prevention || '',
    observations: substance.observations || '',
  })

  const toggleArrayValue = (
    field: 'voies_exposition' | 'epc_existants' | 'epi_portes',
    value: string
  ) => {
    const current = formData[field]
    if (current.includes(value)) {
      setFormData({ ...formData, [field]: current.filter((v) => v !== value) })
    } else {
      setFormData({ ...formData, [field]: [...current, value] })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      setIsSubmitting(true)
      // Convertir pour API - exclure valeurs vides
      const dataToSave: Partial<DuerpSubstance> = {
        ...formData,
        frequence: formData.frequence || undefined,
        forme: formData.forme || undefined,
      }
      await onSave(dataToSave)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Contexte d'exposition
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {substance.nom_substance || substance.cas_number}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Activité */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Activité / Tâche
            </label>
            <input
              type="text"
              value={formData.activite_tache}
              onChange={(e) => setFormData({ ...formData, activite_tache: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: Application peinture au pistolet"
            />
          </div>

          {/* Forme physique et procédés */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Forme physique
              </label>
              <select
                value={formData.forme}
                onChange={(e) => setFormData({ ...formData, forme: e.target.value as FormeType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Sélectionner...</option>
                {FORME_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Procédés d'utilisation
              </label>
              <input
                type="text"
                value={formData.procedes}
                onChange={(e) => setFormData({ ...formData, procedes: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: pulvérisation, chauffage..."
              />
            </div>
          </div>

          {/* Fréquence et durée */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fréquence
              </label>
              <select
                value={formData.frequence}
                onChange={(e) => setFormData({ ...formData, frequence: e.target.value as FrequenceType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">Sélectionner...</option>
                {FREQUENCE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Durée moyenne
              </label>
              <input
                type="text"
                value={formData.duree_moyenne}
                onChange={(e) => setFormData({ ...formData, duree_moyenne: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: 2h/jour"
              />
            </div>
          </div>

          {/* Voies d'exposition */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Voies d'exposition
            </label>
            <div className="flex flex-wrap gap-2">
              {VOIES_EXPOSITION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleArrayValue('voies_exposition', opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm ${
                    formData.voies_exposition.includes(opt.value)
                      ? 'bg-blue-100 text-blue-700 border-blue-300'
                      : 'bg-gray-100 text-gray-600 border-gray-200'
                  } border`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* EPC */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Équipements de protection collective (EPC)
            </label>
            <div className="flex flex-wrap gap-2">
              {EPC_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleArrayValue('epc_existants', opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm ${
                    formData.epc_existants.includes(opt.value)
                      ? 'bg-green-100 text-green-700 border-green-300'
                      : 'bg-gray-100 text-gray-600 border-gray-200'
                  } border`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* EPI */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Équipements de protection individuelle (EPI)
            </label>
            <div className="flex flex-wrap gap-2">
              {EPI_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => toggleArrayValue('epi_portes', opt.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm ${
                    formData.epi_portes.includes(opt.value)
                      ? 'bg-purple-100 text-purple-700 border-purple-300'
                      : 'bg-gray-100 text-gray-600 border-gray-200'
                  } border`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={formData.epi_details}
              onChange={(e) => setFormData({ ...formData, epi_details: e.target.value })}
              className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Précisions EPI (références, matériaux...)"
            />
          </div>

          {/* Mesures de prévention */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mesures de prévention existantes
            </label>
            <textarea
              value={formData.mesures_prevention}
              onChange={(e) => setFormData({ ...formData, mesures_prevention: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Formation, procédures, consignes..."
            />
          </div>

          {/* Observations */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Observations
            </label>
            <textarea
              value={formData.observations}
              onChange={(e) => setFormData({ ...formData, observations: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={2}
              placeholder="Remarques complémentaires..."
            />
          </div>
        </form>

        <div className="p-6 border-t border-gray-200">
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              disabled={isSubmitting}
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
